import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { Sparkles, Mic, Heart, Timer, Target } from "lucide-react";

const ONBOARDING_KEY = 'vyra-onboarding-completed';

interface OnboardingFlowProps {
  onComplete: () => void;
}

const steps = [
  {
    icon: Sparkles,
    title: "Bem-vindo à VYRA! 💜",
    description: "Seu copiloto pessoal para organizar pensamentos e focar no que importa, sem pressão.",
    color: "text-primary"
  },
  {
    icon: Mic,
    title: "Capture rapidamente",
    description: "Clique no botão flutuante para capturar pensamentos, ideias ou tarefas com texto, voz ou imagem.",
    color: "text-blue-500"
  },
  {
    icon: Heart,
    title: "Check-in emocional",
    description: "Registre como você está se sentindo. A VYRA adapta recomendações ao seu estado emocional.",
    color: "text-pink-500"
  },
  {
    icon: Timer,
    title: "Modo Foco Pomodoro",
    description: "Sessões de 5 a 25 minutos com coach motivacional e estatísticas de produtividade.",
    color: "text-green-500"
  },
  {
    icon: Target,
    title: "Jornadas para grandes metas",
    description: "Organize projetos complexos em jornadas com marcos e progresso visual.",
    color: "text-purple-500"
  }
];

export const OnboardingFlow = ({ onComplete }: OnboardingFlowProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed) {
      setTimeout(() => setIsVisible(true), 500);
    }
  }, []);

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
    onComplete();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) return null;

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <Card className="w-full max-w-lg p-8 relative">
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Pular tutorial"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center space-y-6">
          {/* Ícone do passo */}
          <div className="flex justify-center">
            <div className={`w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center ${step.color}`}>
              <Icon className="w-10 h-10" />
            </div>
          </div>

          {/* Conteúdo */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">{step.title}</h2>
            <p className="text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Indicadores de progresso */}
          <div className="flex justify-center gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-8 bg-primary'
                    : 'w-2 bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Navegação */}
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                onClick={handlePrev}
                variant="outline"
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Voltar
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1"
            >
              {currentStep === steps.length - 1 ? (
                'Começar'
              ) : (
                <>
                  Próximo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Texto de pular */}
          <button
            onClick={handleSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Pular tutorial
          </button>
        </div>
      </Card>
    </div>
  );
};
